/**
 * Designed  & Developed by Faisal Nadaf
 * Contact: faisalfnadaf@gmail.com
 *
 * @format
 */

import { ArrowUpRight } from "lucide-react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
	motion,
	useAnimation,
	useInView,
	useReducedMotion,
} from "motion/react";
import { useRef, useEffect } from "react";
import DotGrid from "../DotGrid";

// Each link carries either a `labelKey` (translated) or a literal `label`
// (emails / phone numbers, which must never be translated).
const footerSections = [
	{
		titleKey: "footer.sections.company",
		links: [
			{ labelKey: "common.home", href: "/" },
			{ labelKey: "about.ourStory", href: "/our-story" },
			{ labelKey: "about.achievements", href: "/achievements" },
			{ labelKey: "about.managementTeam", href: "/management-team" },
			{ labelKey: "nav.worldwideProjects", href: "/worldwide-projects" },
			{ labelKey: "nav.clientele", href: "/clientele" },
			{ labelKey: "about.certifications", href: "/certifications" },
		],
	},
	{
		titleKey: "footer.sections.product",
		links: [
			{
				labelKey: "productsMenu.shellTube",
				href: "/products/shell-tube-heat-exchangers",
			},
			{
				labelKey: "productsMenu.airCooled",
				href: "/products/air-cooled-heat-exchangers",
			},
			{
				labelKey: "productsMenu.waterCooled",
				href: "/products/water-cooled-air-coolers",
			},
			{
				labelKey: "productsMenu.doublePipe",
				href: "/products/double-pipe-heat-exchangers",
			},
			{
				labelKey: "productsMenu.recuperatorsWasteHeat",
				href: "/products/recuperators",
			},
			{
				labelKey: "productsMenu.dryCoolers",
				href: "/products/dry-coolers",
			},
			{
				labelKey: "productsMenu.onan",
				href: "/products/onan",
			},
		],
	},
	{
		titleKey: "footer.sections.industries",
		links: [
			{
				labelKey: "industries.powerGeneration",
				href: "/industries/power-generation",
			},
			{ labelKey: "industries.oilGas", href: "/industries/oil-gas" },
			{
				labelKey: "industries.renewableEnergy",
				href: "/industries/renewable-energy",
			},
			{ labelKey: "industries.dataCenter", href: "/industries/data-center" },
			{
				labelKey: "industries.waterTreatment",
				href: "/industries/water-treatment",
			},
			{
				labelKey: "industries.steelMetallurgy",
				href: "/industries/steel-metallurgy",
			},
			{
				labelKey: "industries.chemicalProcessing",
				href: "/industries/chemical-fertilizer",
			},
		],
	},
	{
		titleKey: "footer.sections.contact",
		links: [
			{
				labelKey: "footer.contact.manufacturingOffice",
				href: "https://maps.app.goo.gl/GjaBT1urZssHa3di7",
			},
			{ label: "info@positron-he.com", href: "mailto:info@positron-he.com" },
			{ label: "+91 831-2411835", href: "tel:+918312411835" },
			{ label: "", href: "" },
			{
				labelKey: "footer.contact.uaeOffice",
				href: "https://maps.app.goo.gl/jPVzGHmK45e3Kqe46",
			},
			{ label: "myv@positron-he.com", href: "mailto:myv@positron-he.com" },
			{ label: "+971 54 275 5275", href: "tel:+971 54 275 5275" },
		],
	},
] as const;

const socialLinks = [
	// { name: "Twitter", href: "" },
	{
		name: "LinkedIn",
		href: "https://www.linkedin.com/company/positron-engineering-corporation/",
	},
];

export function FooterSection() {
	const { t } = useTranslation();
	const footerRef = useRef<HTMLElement>(null);
	const controls = useAnimation();
	const shouldReduceMotion = useReducedMotion();
	const isInView = useInView(footerRef, { once: true, amount: 0.15 });

	useEffect(() => {
		if (isInView) {
			controls.start("visible");
		}
	}, [isInView, controls]);

	const containerVariants = {
		hidden: { opacity: 0 },
		visible: {
			opacity: 1,
			transition: {
				staggerChildren: shouldReduceMotion ? 0 : 0.08,
				delayChildren: 0.1,
			},
		},
	};

	const itemVariants = {
		hidden: { opacity: 0, y: shouldReduceMotion ? 0 : 16 },
		visible: {
			opacity: 1,
			y: 0,
			transition: { duration: 0.5, ease: [0.22, 1, 0.36, 1] },
		},
	};

	return (
		<motion.footer
			ref={footerRef}
			initial="hidden"
			animate={controls}
			variants={containerVariants}
			className="relative border-t border-white bg-[#3b0092]">
			<div className="absolute inset-0">
				<DotGrid
					dotSize={4}
					gap={15}
					baseColor="#2c016c"
					activeColor="#f09a38"
					proximity={100}
					shockRadius={200}
					shockStrength={3}
					resistance={750}
					returnDuration={1.5}
				/>
			</div>

			<div className="site-container relative z-10 px-4 sm:px-6 md:px-8 lg:px-12 mx-auto max-w-7xl w-full">
				{/* Mobile splits the four link groups into two readable columns instead
				    of one very long ribbon; `sm` and up keep the original 1-then-6
				    column grid untouched. */}
				<motion.div
					variants={containerVariants}
					className="grid grid-cols-2 gap-x-6 gap-y-8 py-10 sm:grid-cols-1 sm:gap-10 md:gap-12 sm:py-12 md:py-16 lg:grid-cols-6 lg:gap-12 lg:py-20">
					<motion.div
						variants={itemVariants}
						className="col-span-2 sm:col-span-1 md:col-span-2">
						<Link
							to="/"
							className="mb-4 sm:mb-5 inline-flex items-center gap-2 rounded-xl bg-white px-3 py-2 shadow-[0_4px_14px_rgba(0,0,0,0.12)]">
							<img
								className="h-6 sm:h-7 md:h-10"
								src="/images/logo1.png"
								alt="Positron"
							/>
						</Link>

						<p className="max-w-sm text-xs sm:text-sm leading-relaxed text-white/80">
							{t("footer.tagline")}
						</p>

						{/* Social — the official LinkedIn mark stands on its own here; a
						    surrounding pill would only compete with the glyph's own badge. */}
						<div className="mt-5 flex gap-3 sm:mt-6 sm:gap-4">
							{socialLinks.map((link) => (
								<motion.a
									key={link.name}
									href={link.href}
									target="_blank"
									rel="noopener noreferrer"
									variants={itemVariants}
									whileHover={shouldReduceMotion ? {} : { y: -2 }}
									whileTap={shouldReduceMotion ? {} : { scale: 0.94 }}
									transition={{ type: "spring", stiffness: 400, damping: 22 }}
									aria-label={link.name}
									title={link.name}
									className="touch-target group inline-flex items-center justify-center rounded-lg opacity-90 transition-opacity duration-200 hover:opacity-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#f09a38] focus-visible:ring-offset-2 focus-visible:ring-offset-[#3b0092]">
									<img
										src="/images/logos/linkedin.png"
										alt=""
										aria-hidden="true"
										loading="lazy"
										className="h-9 w-9 object-contain sm:h-11 sm:w-11"
									/>
								</motion.a>
							))}
						</div>
					</motion.div>

					{footerSections.map((section) => {
						// Two short groups pair up as mobile half-columns; Industries and
						// Contact take the full width instead — Industries because a lone
						// half column would leave a ragged gap (its list splits into two
						// text columns to compensate), Contact because it carries full
						// addresses, emails and phone numbers.
						const fullWidthOnMobile =
							section.titleKey === "footer.sections.contact" ||
							section.titleKey === "footer.sections.industries";
						const splitList = section.titleKey === "footer.sections.industries";
						return (
							<motion.div
								key={section.titleKey}
								variants={itemVariants}
								className={
									fullWidthOnMobile ? "col-span-2 sm:col-span-1" : "col-span-1"
								}>
								<h3 className="mb-2.5 text-[11px] font-semibold uppercase tracking-[0.18em] text-white/70 sm:mb-4 sm:text-xs md:mb-5">
									{t(section.titleKey)}
								</h3>
								<ul
									className={`footer-links space-y-0.5 sm:space-y-3 ${
										splitList ? "max-sm:columns-2 max-sm:gap-x-6" : ""
									}`}>
									{section.links.map((link, idx) => {
										const label =
											"labelKey" in link ? t(link.labelKey) : link.label;
										// A blank entry is a deliberate spacer between the two
										// office blocks; rendering an empty <a> would be an
										// unlabelled link for screen readers.
										if (!label) {
											return (
												<li
													key={`${section.titleKey}-${idx}`}
													aria-hidden="true"
													className="h-2 sm:h-3"
												/>
											);
										}
										return (
											<li key={`${section.titleKey}-${idx}`}>
												<a
													href={link.href}
													className="inline-flex items-center text-[13px] leading-snug text-white/80 transition-colors hover:text-white sm:text-sm">
													{label}
												</a>
											</li>
										);
									})}
								</ul>
							</motion.div>
						);
					})}
				</motion.div>

				<motion.div
					variants={itemVariants}
					className="flex flex-col items-center gap-3 border-t border-white/ py-5 text-xs text-white/80 sm:flex-row sm:items-center sm:justify-between sm:gap-4 sm:py-6 sm:text-sm md:py-7">
					<p className="text-center">
						{new Date().getFullYear()} {t("footer.copyright")}
						<motion.a
							href="https://cubiccode.in/"
							variants={itemVariants}
							whileHover={shouldReduceMotion ? {} : { x: 3 }}
							className="group inline-flex items-center gap-1 text-xs sm:text-sm text-white/80 transition-colors ml-1  hover:text-white font-bold">
							Cubiccode
							<ArrowUpRight className="h-3 w-3 sm:h-3.5 sm:w-3.5 opacity-50 transition-all duration-200 group-hover:translate-x-0.5 group-hover:opacity-100" />
						</motion.a>
					</p>

					<span className="inline-flex items-center gap-2">
						<span className="h-2 w-2 rounded-full bg-green-500" />
						<span className="hidden sm:inline">{t("footer.status")}</span>
					</span>
				</motion.div>
			</div>
		</motion.footer>
	);
}
